/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TraceTrackCards.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Grid,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import TraceTrackCardWithTitle from "./TraceTrackCardWithTitle";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import { useDispatch, useSelector } from "react-redux";
import { componentStyle } from "../../../config/appTheme";
import ModuleViewMoreDialog from "../../../components/customComponentsV2/dashboard/pages/tickets/ModuleViewMoreDialog";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import { HandleApiActions, labCar } from "../../../redux/actions";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import LinkedTestCaseComponent from "./LinkedTestCaseComponent";
import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";

const toArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter(Boolean);
  return [data];
};

function TraceTrackCards({ selectedTrackTraceDetails, projectID, variantID }) {
  const profileDetails = useSelector((store) => store.profileReducer);
  const dispatch = useDispatch();

  const menuItems = [
    {
      id: "ticketDetails",
      label: "Ticket Details",
      component: "ArchitectureTypes",
    },
    {
      id: "legalDetails",
      label: "Legal Details",
      component: "ManufacturerDetails",
    },
    {
      id: "componentDetails",
      label: "Component Details",
      component: "PowerTrainClass",
    },
    {
      id: "parentComponent",
      label: "Parent Component Details",
      component: "PowerTrainClass",
    },
    {
      id: "projectDetails",
      label: "Project Details",
      component: "PowerTrainClass",
    },
    {
      id: "linkedComponent",
      label: "Linked Components",
      component: "PowerTrainClass",
    },
    {
      id: "linkedTestCases",
      label: "Linked Test Cases",
      component: "PowerTrainClass",
    },
    {
      id: "users",
      label: "Users",
      component: "PowerTrainClass",
    },
    {
      id: "attributes",
      label: "Attributes",
      component: "PowerTrainClass",
    },
  ];

  const [selectedMenuItem, setSelectedMenuItem] = useState("ticketDetails");
  const [componentSelfData, setComponentSelfData] = useState({});
  const [page, setPage] = useState(1);
  const rowsPerPage = 25;
  const [slicedAttributes, setSlicedAttributes] = useState([]);
  const handleMenuItemClick = (menuId) => {
    setSelectedMenuItem(menuId);
  };

  const lsList = useMemo(
    () => toArray(selectedTrackTraceDetails?.ls_details),
    [selectedTrackTraceDetails?.ls_details],
  );
  const lrList = useMemo(
    () => toArray(selectedTrackTraceDetails?.lr_details),
    [selectedTrackTraceDetails?.lr_details],
  );
  const eeList = useMemo(
    () => toArray(selectedTrackTraceDetails?.ee_details),
    [selectedTrackTraceDetails?.ee_details],
  );
  const swmList = useMemo(
    () => toArray(selectedTrackTraceDetails?.swm_details),
    [selectedTrackTraceDetails?.swm_details],
  );
  const vsList = useMemo(
    () => toArray(selectedTrackTraceDetails?.vehicle_system_details),
    [selectedTrackTraceDetails?.vehicle_system_details],
  );
  const vcList = useMemo(
    () => toArray(selectedTrackTraceDetails?.vehicle_component_details),
    [selectedTrackTraceDetails?.vehicle_component_details],
  );

  const lsOptions = useMemo(() => {
    return lsList.map((ls, index) => {
      let label = `Standard ${index + 1}`;
      if (ls?.standard_number) {
        label = String(ls.standard_number);
      } else if (ls?.name) {
        label = String(ls.name);
      }
      return {
        label,
        value: ls?.id ?? ls?.ls_id ?? ls?.standard_number ?? index,
        data: ls,
      };
    });
  }, [lsList]);

  const lrOptions = useMemo(() => {
    return lrList.map((lr, index) => {
      let label = `Requirement ${index + 1}`;
      if (lr?.req_number) {
        label = String(lr.req_number);
      } else if (lr?.chapter) {
        label = String(lr.chapter);
      }
      return {
        label,
        value: lr?.id ?? lr?.lr_id ?? lr?.req_number ?? index,
        data: lr,
      };
    });
  }, [lrList]);

  const eeOptions = useMemo(() => {
    return eeList.map((ee, index) => {
      let label = `EE ${index + 1}`;
      if (ee?.assembly_number) {
        label = String(ee.assembly_number);
      } else if (ee?.name) {
        label = String(ee.name);
      }
      return {
        label,
        value: ee?.ee_id ?? ee?.id ?? ee?.assembly_number ?? index,
        data: ee,
      };
    });
  }, [eeList]);

  const swmOptions = useMemo(() => {
    return swmList.map((swm, index) => ({
      label: swm?.name ? String(swm.name) : `SWM ${index + 1}`,
      value: swm?.swm_id ?? swm?.id ?? swm?.name ?? index,
      data: swm,
    }));
  }, [swmList]);

  const vsOptions = useMemo(() => {
    return vsList.map((vs, index) => ({
      label: vs?.name ? String(vs.name) : `VS ${index + 1}`,
      value: vs?.vs_id ?? vs?.id ?? vs?.name ?? index,
      data: vs,
    }));
  }, [vsList]);

  const vcOptions = useMemo(() => {
    return vcList.map((vc, index) => {
      let label = `VC ${index + 1}`;
      if (vc?.name) {
        label = String(vc.name);
      } else if (vc?.component_number) {
        label = String(vc.component_number);
      }
      return {
        label,
        value: vc?.vc_id ?? vc?.id ?? vc?.component_number ?? index,
        data: vc,
      };
    });
  }, [vcList]);

  const [selectedLs, setSelectedLs] = useState("");
  const [selectedLr, setSelectedLr] = useState("");
  const [selectedEe, setSelectedEe] = useState("");
  const [selectedSwm, setSelectedSwm] = useState("");
  const [selectedVs, setSelectedVs] = useState("");
  const [selectedVc, setSelectedVc] = useState("");

  useEffect(() => {
    if (lsOptions.length > 0) {
      setSelectedLs((prev) => {
        const exists = lsOptions.some((opt) => opt.value === prev);
        return exists ? prev : lsOptions[0]?.value;
      });
    } else {
      setSelectedLs("");
    }
  }, [lsOptions]);

  useEffect(() => {
    if (lrOptions.length > 0) {
      setSelectedLr((prev) => {
        const exists = lrOptions.some((opt) => opt.value === prev);
        return exists ? prev : lrOptions[0]?.value;
      });
    } else {
      setSelectedLr("");
    }
  }, [lrOptions]);

  useEffect(() => {
    if (eeOptions.length > 0) {
      setSelectedEe((prev) => {
        const exists = eeOptions.some((opt) => opt.value === prev);
        return exists ? prev : eeOptions[0]?.value;
      });
    } else {
      setSelectedEe("");
    }
  }, [eeOptions]);

  useEffect(() => {
    if (swmOptions.length > 0) {
      setSelectedSwm((prev) => {
        const exists = swmOptions.some((opt) => opt.value === prev);
        return exists ? prev : swmOptions[0]?.value;
      });
    } else {
      setSelectedSwm("");
    }
  }, [swmOptions]);

  useEffect(() => {
    if (vsOptions.length > 0) {
      setSelectedVs((prev) => {
        const exists = vsOptions.some((opt) => opt.value === prev);
        return exists ? prev : vsOptions[0]?.value;
      });
    } else {
      setSelectedVs("");
    }
  }, [vsOptions]);

  useEffect(() => {
    if (vcOptions.length > 0) {
      setSelectedVc((prev) => {
        const exists = vcOptions.some((opt) => opt.value === prev);
        return exists ? prev : vcOptions[0]?.value;
      });
    } else {
      setSelectedVc("");
    }
  }, [vcOptions]);

  const activeLs = useMemo(() => {
    return lsOptions.find((opt) => opt.value === selectedLs)?.data || lsList[0];
  }, [lsOptions, selectedLs, lsList]);

  const activeLr = useMemo(() => {
    return lrOptions.find((opt) => opt.value === selectedLr)?.data || lrList[0];
  }, [lrOptions, selectedLr, lrList]);

  const activeEe = useMemo(() => {
    return eeOptions.find((opt) => opt.value === selectedEe)?.data || eeList[0];
  }, [eeOptions, selectedEe, eeList]);

  const activeSwm = useMemo(() => {
    return swmOptions.find((opt) => opt.value === selectedSwm)?.data || swmList[0];
  }, [swmOptions, selectedSwm, swmList]);

  const activeVs = useMemo(() => {
    return vsOptions.find((opt) => opt.value === selectedVs)?.data || vsList[0];
  }, [vsOptions, selectedVs, vsList]);

  const activeVc = useMemo(() => {
    return vcOptions.find((opt) => opt.value === selectedVc)?.data || vcList[0];
  }, [vcOptions, selectedVc, vcList]);

  useEffect(() => {
    setPage(1);
  }, [selectedEe, selectedSwm, selectedVs, selectedVc]);

  const getTicketType = useCallback(() => {
    switch (Number(selectedTrackTraceDetails?.ticket_type)) {
      case 1:
        return "SWM";
      case 2:
        return "EE";
      case 3:
        return "VC";
      case 6:
        return "VS";
      default:
        return "";
    }
  }, [selectedTrackTraceDetails?.ticket_type]);

  const getApiAction = useCallback(() => {
    switch (Number(selectedTrackTraceDetails?.ticket_type)) {
      case 1:
        return {
          apiAction: softwareIntersectionAPI.get_software_intersections,
          apiDataKey: "linked_swm_details",
        };
      case 2:
        return {
          apiAction: labCar.get_ee_intersections,
          apiDataKey: "linked_ee_details",
        };
      default:
        return {};
    }
  }, [selectedTrackTraceDetails?.ticket_type]);

  const getComponentApiByType = useCallback(() => {
    const vehicleSystemId = activeVs?.vs_id ?? activeVs?.id;
    const vehicleComponentId = activeVc?.vc_id ?? activeVc?.id;
    const eeId = activeEe?.ee_id ?? activeEe?.id;
    const swmId = activeSwm?.swm_id ?? activeSwm?.id;

    switch (getTicketType()) {
      case "EE":
        return {
          apiAction: labCar?.get_ee_components,
          componentFunctionKey: "vehicle_system_details",
          searchKeys: ["name"],
          disableApiCall: !eeId,
          headder: {
            name: componentSelfData.assembly_number || activeEe?.assembly_number || "-",
            version: componentSelfData.version || activeEe?.version || "-",
            description: componentSelfData.description || activeEe?.description || "-",
          },
          componentKey: "ee_details",
          linkedArrayKey: "linked_ee_ids",
          idKey: "ee_id",
          reqObj: {
            ee_id: eeId,
            ...(swmId && {
              swm_id: swmId,
            }),
          },
        };
      case "SWM":
        return {
          apiAction: labCar?.get_sw_modules,
          componentFunctionKey: "vehicle_functions",
          searchKeys: ["name", "abbreviation"],
          disableApiCall: !swmId,
          headder: {
            name: componentSelfData.name || activeSwm?.name || "-",
            version: componentSelfData.version || activeSwm?.version || "-",
            description: componentSelfData.description || activeSwm?.description || "-",
          },
          componentKey: "swm_details",
          linkedArrayKey: "linked_swm_ids",
          idKey: "swm_id",
          reqObj: {
            ee_id: eeId,
            ...(swmId && {
              swm_id: swmId,
            }),
          },
        };
      case "VC":
        return {
          apiAction: labCar.get_vehicle_components,
          searchKeys: ["description"],
          disableApiCall: !vehicleComponentId,
          headder: {
            name: componentSelfData.name || activeVc?.name || "-",
            version: componentSelfData.version || activeVc?.version || "-",
            description: componentSelfData.description || activeVc?.description || "-",
          },
          componentKey: "vehicle_component_details",
          idKey: "id",
          reqObj: {
            ...(vehicleSystemId != null && { vs_id: vehicleSystemId }),
            ...(vehicleComponentId != null && { vc_id: vehicleComponentId }),
          },
        };
      case "VS":
        return {
          apiAction: labCar.get_vehicle_systems,
          searchKeys: ["name"],
          disableApiCall: !vehicleSystemId,
          headder: {
            name: componentSelfData.name || activeVs?.name || "-",
            version: componentSelfData.version || activeVs?.version || "-",
            description: componentSelfData.description || activeVs?.description || "-",
          },
          componentKey: "vehicle_system_details",
          idKey: "id",
          reqObj: {
            ...(vehicleSystemId != null && { vs_id: vehicleSystemId }),
          },
        };
      default:
        return {};
    }
  }, [
    getTicketType,
    activeEe,
    activeSwm,
    activeVs,
    activeVc,
    componentSelfData.assembly_number,
    componentSelfData.version,
    componentSelfData.description,
    componentSelfData.name,
  ]);

  const getComponentDetails = useCallback(async () => {
    try {
      if (!projectID && !variantID && getComponentApiByType().disableApiCall)
        return;

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...getComponentApiByType()?.apiAction,
            params: {
              project_id: projectID,
              variant_id: variantID,
              ...getComponentApiByType()?.reqObj,
              page_number: page,
              page_size: rowsPerPage,
            },
            show_toast: false,
          }),
        ),
      );

      if (resp?.error) {
        setComponentSelfData({});
      } else {
        setComponentSelfData(resp?.data?.[0] || {});
      }
    } catch {
      setComponentSelfData({});
    }
  }, [projectID, variantID, getComponentApiByType, dispatch, page, rowsPerPage]);

  const getLinkedComponentDialogProps = () => {
    const vehicleSystemId = activeVs?.vs_id ?? activeVs?.id;
    const vehicleComponentId = activeVc?.vc_id ?? activeVc?.id;
    const eeId = activeEe?.ee_id ?? activeEe?.id;
    const swmId = activeSwm?.swm_id ?? activeSwm?.id;

    switch (getTicketType()) {
      case "VS":
        return {
          apiAction: labCar.get_vehicle_systems,
          apiDataKey: "linked_vs_details",
          type: "VS",
          linkApiProps: {
            vs_id: vehicleSystemId,
          },
        };
      case "VC":
        return {
          apiAction: labCar.get_vehicle_components,
          apiDataKey: "linked_vc_details",
          type: "VC",
          linkApiProps: {
            vc_id: vehicleComponentId,
            vs_id: vehicleSystemId,
          },
        };
      case "EE":
        return {
          apiAction: labCar.get_ee_intersections,
          apiDataKey: "linked_ee_details",
          type: "EE",
          linkApiProps: {
            ee_id: eeId,
          },
        };
      case "SWM":
        return {
          apiAction: softwareIntersectionAPI.get_software_intersections,
          apiDataKey: "linked_swm_details",
          type: "SWM",
          linkApiProps: {
            swm_id: swmId,
            ee_id: eeId,
          },
        };
      default:
        return null;
    }
  };

  const getLinkedComponents = useCallback(async () => {
    try {
      if (!projectID && !variantID && getComponentApiByType().disableApiCall) {
        return;
      }

      const apiDetails = getApiAction().apiAction;
      if (!apiDetails) {
        return;
      }

      const eeId = activeEe?.ee_id ?? activeEe?.id;
      const swmId = activeSwm?.swm_id ?? activeSwm?.id;

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: projectID,
              variant_id: variantID,
              ee_id: eeId,
              ...(swmId && {
                swm_id: swmId,
              }),
            },
            show_toast: false,
          }),
        ),
      );
    } catch {
      // Ignore API fetch errors cleanly
    }
  }, [
    projectID,
    variantID,
    getComponentApiByType,
    getApiAction,
    dispatch,
    activeEe,
    activeSwm,
  ]);

  const renderComponentSelector = (labelPrefix = "") => {
    switch (getTicketType()) {
      case "EE":
        if (eeList.length === 0) return null;
        return (
          <Box sx={{ mb: 2 }}>
            <CustomSelectV2
              label={`${labelPrefix}EE Component`}
              required={false}
              optionsList={eeOptions}
              selectedValue={selectedEe}
              handleOnChange={(e) => setSelectedEe(e.target.value)}
            />
          </Box>
        );
      case "SWM":
        if (swmList.length === 0) return null;
        return (
          <Box sx={{ mb: 2 }}>
            <CustomSelectV2
              label={`${labelPrefix}Software Module`}
              required={false}
              optionsList={swmOptions}
              selectedValue={selectedSwm}
              handleOnChange={(e) => setSelectedSwm(e.target.value)}
            />
          </Box>
        );
      case "VC":
        if (vcList.length === 0) return null;
        return (
          <Box sx={{ mb: 2 }}>
            <CustomSelectV2
              label={`${labelPrefix}Vehicle Component`}
              required={false}
              optionsList={vcOptions}
              selectedValue={selectedVc}
              handleOnChange={(e) => setSelectedVc(e.target.value)}
            />
          </Box>
        );
      case "VS":
        if (vsList.length === 0) return null;
        return (
          <Box sx={{ mb: 2 }}>
            <CustomSelectV2
              label={`${labelPrefix}Vehicle System`}
              required={false}
              optionsList={vsOptions}
              selectedValue={selectedVs}
              handleOnChange={(e) => setSelectedVs(e.target.value)}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    getComponentDetails();
    getLinkedComponents();
  }, [getComponentDetails, getLinkedComponents]);

  useEffect(() => {
    const attributes = componentSelfData?.attributes || [];
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    setSlicedAttributes(attributes.slice(startIndex, endIndex));
  }, [page, rowsPerPage, componentSelfData?.attributes]);

  return (
    <Grid container>
      <Grid item sm={3}>
        {menuItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItemButton
              onClick={() => handleMenuItemClick(item.id)}
              sx={{
                ...styled.menuItem,
                backgroundColor:
                  selectedMenuItem === item.id ? "#000000" : "#ffffff",
                color: selectedMenuItem === item.id ? "#ffffff" : "#000000",
                "&:hover": {
                  backgroundColor: "#000000",
                  color: "#ffffff",
                  "& .MuiTypography-root": {
                    color: "#ffffff",
                  },
                },
                borderTop: "1px solid black",
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ ...styled.menuItemContent }}>
                    <Typography
                      sx={{
                        ...styled.menuText,
                        color:
                          selectedMenuItem === item.id ? "#ffffff" : "#000000",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography>
                      <ArrowRightAltIcon />
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </React.Fragment>
        ))}
      </Grid>
      <Grid item sm={9}>
        <Box
          sx={{
            height: "430px",
            overflow: "auto",
            ...componentStyle.scroll,
          }}
        >
          {selectedMenuItem === "ticketDetails" && (
            <TraceTrackCardWithTitle
              title="Ticket Details"
              data={[
                {
                  key: "Ticket ID",
                  value: selectedTrackTraceDetails?.ticket_id,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Ticket Status",
                  value: selectedTrackTraceDetails?.status,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Description",
                  value: selectedTrackTraceDetails?.description,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Reason",
                  value: selectedTrackTraceDetails?.reason,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Created At",
                  value: convertUtcToTimeZone(
                    selectedTrackTraceDetails?.created_at,
                    profileDetails?.profile?.profile?.region,
                  ),
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Changed At",
                  value: convertUtcToTimeZone(
                    selectedTrackTraceDetails?.updated_at,
                    profileDetails?.profile?.profile?.region,
                  ),
                  keyStyle: { fontWeight: 600 },
                },
              ]}
            />
          )}

          {selectedMenuItem === "legalDetails" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 1,
                ...componentStyle.scroll,
              }}
            >
              {lsList.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <CustomSelectV2
                    label="Legal Standard"
                    required={false}
                    optionsList={lsOptions}
                    selectedValue={selectedLs}
                    handleOnChange={(e) => setSelectedLs(e.target.value)}
                  />
                  {activeLs && (
                    <TraceTrackCardWithTitle
                      title="Legal Standard Details"
                      data={[
                        {
                          key: "Standard No",
                          value: activeLs?.standard_number,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Name",
                          value: activeLs?.name,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Revision",
                          value: activeLs?.revision,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Description",
                          value: activeLs?.description,
                          keyStyle: { fontWeight: 600 },
                          length: 150,
                        },
                      ]}
                    />
                  )}
                </Box>
              )}

              {lrList.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <CustomSelectV2
                    label="Legal Requirement"
                    required={false}
                    optionsList={lrOptions}
                    selectedValue={selectedLr}
                    handleOnChange={(e) => setSelectedLr(e.target.value)}
                  />
                  {activeLr && (
                    <TraceTrackCardWithTitle
                      title="Legal Requirement Details"
                      data={[
                        {
                          key: "Chapter",
                          value: activeLr?.chapter,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Requirement No",
                          value: activeLr?.req_number,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Description",
                          value: activeLr?.description,
                          keyStyle: { fontWeight: 600 },
                          length: 150,
                        },
                      ]}
                    />
                  )}
                </Box>
              )}

              {lsList.length === 0 && lrList.length === 0 && (
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    No legal details available.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {selectedMenuItem === "componentDetails" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 1,
                ...componentStyle.scroll,
              }}
            >
              {eeList.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <CustomSelectV2
                    label="EE Component"
                    required={false}
                    optionsList={eeOptions}
                    selectedValue={selectedEe}
                    handleOnChange={(e) => setSelectedEe(e.target.value)}
                  />
                  {activeEe && (
                    <TraceTrackCardWithTitle
                      title="EE Component"
                      data={[
                        {
                          key: "Assembly No",
                          value: activeEe?.assembly_number,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Version",
                          value: activeEe?.version,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Description",
                          value: activeEe?.description,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Supplier Name",
                          value: activeEe?.supplier_name,
                          keyStyle: { fontWeight: 600 },
                        },
                      ]}
                    />
                  )}
                </Box>
              )}

              {swmList.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <CustomSelectV2
                    label="Software Module"
                    required={false}
                    optionsList={swmOptions}
                    selectedValue={selectedSwm}
                    handleOnChange={(e) => setSelectedSwm(e.target.value)}
                  />
                  {activeSwm && (
                    <TraceTrackCardWithTitle
                      title="Software Module"
                      data={[
                        {
                          key: "Software Module",
                          value: activeSwm?.name,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Version",
                          value: activeSwm?.version,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Description",
                          value: activeSwm?.description,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Summary",
                          value: activeSwm?.summary,
                          keyStyle: { fontWeight: 600 },
                        },
                      ]}
                    />
                  )}
                </Box>
              )}

              {vsList.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <CustomSelectV2
                    label="Vehicle System"
                    required={false}
                    optionsList={vsOptions}
                    selectedValue={selectedVs}
                    handleOnChange={(e) => setSelectedVs(e.target.value)}
                  />
                  {activeVs && (
                    <TraceTrackCardWithTitle
                      title="Vehicle System Details"
                      data={[
                        {
                          key: "Name",
                          value: activeVs?.name,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Description",
                          value: activeVs?.description,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Suppliers",
                          value:
                            activeVs?.supplier_name ||
                            selectedTrackTraceDetails?.supplier_name ||
                            "-",
                          keyStyle: { fontWeight: 600 },
                        },
                      ]}
                    />
                  )}
                </Box>
              )}

              {vcList.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <CustomSelectV2
                    label="Vehicle Component"
                    required={false}
                    optionsList={vcOptions}
                    selectedValue={selectedVc}
                    handleOnChange={(e) => setSelectedVc(e.target.value)}
                  />
                  {activeVc && (
                    <TraceTrackCardWithTitle
                      title="Vehicle Component Details"
                      data={[
                        {
                          key: "Name",
                          value: activeVc?.name,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Component No",
                          value: activeVc?.component_number,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Description",
                          value: activeVc?.description,
                          keyStyle: { fontWeight: 600 },
                        },
                        {
                          key: "Suppliers",
                          value:
                            activeVc?.supplier_name ||
                            selectedTrackTraceDetails?.supplier_name ||
                            "-",
                          keyStyle: { fontWeight: 600 },
                        },
                      ]}
                    />
                  )}
                </Box>
              )}

              {eeList.length === 0 &&
                swmList.length === 0 &&
                vsList.length === 0 &&
                vcList.length === 0 && (
                  <Box
                    sx={{
                      p: 1.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      No component details available.
                    </Typography>
                  </Box>
                )}
            </Box>
          )}

          {selectedMenuItem === "parentComponent" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              <Box sx={{ textAlign: "left", mt: 2 }}>
                <Box>
                  <Typography sx={{ ...componentStyle.font.cardHeadder }}>
                    Project Details
                  </Typography>
                  <Typography sx={{ ...componentStyle.font.text }}>
                    {selectedTrackTraceDetails?.project_details?.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ ...componentStyle.font.cardHeadder }}>
                    Variant Details
                  </Typography>
                  <Typography sx={{ ...componentStyle.font.text }}>
                    {selectedTrackTraceDetails?.variant_details?.[0]?.name}
                  </Typography>
                </Box>
                {eeList[0]?.zone_details?.zone_name && (
                  <Box>
                    <Typography sx={{ ...componentStyle.font.cardHeadder }}>
                      Zone Name
                    </Typography>
                    <Typography sx={{ ...componentStyle.font.text }}>
                      {eeList[0]?.zone_details?.zone_name}
                    </Typography>

                    {vcList[0]?.id && (
                      <TraceTrackCardWithTitle
                        title={"Vehicle Component"}
                        data={[
                          {
                            key: "Name",
                            value: vcList[0]?.name,
                            keyStyle: { fontWeight: 600 },
                          },

                          {
                            key: "Description",
                            value: vcList[0]?.description,
                            keyStyle: { fontWeight: 600 },
                          },
                        ]}
                      />
                    )}
                  </Box>
                )}

                {swmList[0]?.swm_id && (
                  <TraceTrackCardWithTitle
                    title={"EE Component"}
                    data={[
                      {
                        key: "Assembly No",
                        value: eeList[0]?.assembly_number,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Version",
                        value: eeList[0]?.version,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Description",
                        value: eeList[0]?.description,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Supplier Name",
                        value: eeList[0]?.supplier_name,
                        keyStyle: { fontWeight: 600 },
                      },
                    ]}
                  />
                )}
              </Box>
            </Box>
          )}

          {selectedMenuItem === "projectDetails" && (
            <Box>
              <TraceTrackCardWithTitle
                title={"Project Details"}
                data={[
                  {
                    key: "Name",
                    value: selectedTrackTraceDetails?.project_details?.name,
                    keyStyle: { fontWeight: 600 },
                  },
                  {
                    key: "Architecture Type",
                    value:
                      selectedTrackTraceDetails?.project_details
                        ?.architecture_name,
                    keyStyle: { fontWeight: 600 },
                  },
                ]}
              />
              <TraceTrackCardWithTitle
                title={"Variant Details"}
                data={[
                  {
                    key: "Name",
                    value:
                      selectedTrackTraceDetails?.variant_details?.[0]?.name,
                    keyStyle: { fontWeight: 600 },
                  },
                  {
                    key: "Milestone Names",
                    value: selectedTrackTraceDetails?.variant_details?.[0]?.milestone_details
                      ?.map((milestone) => milestone.milestone_name)
                      .join(", "),
                    keyStyle: { fontWeight: 600 },
                  },
                ]}
              />
            </Box>
          )}

          {selectedMenuItem === "linkedComponent" && (
            <Box
              sx={{
                height: "415px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              {renderComponentSelector()}
              {getLinkedComponentDialogProps()?.type ? (
                <ModuleViewMoreDialog
                  key={`${getTicketType()}-${selectedEe}-${selectedSwm}-${selectedVs}-${selectedVc}`}
                  isEEDialogOpen={true}
                  toggleEEDialog={() => () => {}}
                  selectedComponent={{
                    ...selectedTrackTraceDetails,
                    ee_details: activeEe,
                    swm_details: activeSwm,
                    vehicle_system_details: activeVs,
                    vehicle_component_details: activeVc,
                  }}
                  linkApiProps={getLinkedComponentDialogProps()?.linkApiProps}
                  apiAction={getLinkedComponentDialogProps()?.apiAction}
                  apiDataKey={getLinkedComponentDialogProps()?.apiDataKey}
                  type={getLinkedComponentDialogProps()?.type}
                  inline
                  projectId={projectID}
                  variantId={variantID}
                />
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    No linked component data available.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {selectedMenuItem === "linkedTestCases" && (
            <LinkedTestCaseComponent
              linkedTestCases={selectedTrackTraceDetails?.test_case_details}
            />
          )}

          {selectedMenuItem === "users" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              {selectedTrackTraceDetails?.oem_user_details?.map(
                (user, index) => {
                  const userKey = user?.id || user?.email || `oem-user-${index}`;
                  return (
                    <TraceTrackCardWithTitle
                      key={userKey}
                      title={"Compliance Requestors"}
                      data={[
                        {
                          key: `${index + 1} Name`,
                          value: user?.name,
                          keyStyle: { fontWeight: 600 },
                          displayInSingleLine: true,
                        },
                        {
                          key: "Email",
                          value: user?.email,
                          keyStyle: { fontWeight: 600, ml: "12px" },
                          displayInSingleLine: true,
                        },
                      ]}
                    />
                  );
                },
              )}
              {selectedTrackTraceDetails?.tech_user_details?.map(
                (user, index) => {
                  const userKey = user?.id || user?.email || `tech-user-${index}`;
                  return (
                    <TraceTrackCardWithTitle
                      key={userKey}
                      title={"Compliance Reviewers"}
                      data={[
                        {
                          key: `${index + 1} Name`,
                          value: user?.name,
                          keyStyle: { fontWeight: 600 },
                          displayInSingleLine: true,
                        },
                        {
                          key: "Email",
                          value: user?.email,
                          keyStyle: { fontWeight: 600, ml: "12px" },
                          displayInSingleLine: true,
                        },
                      ]}
                    />
                  );
                },
              )}
              {selectedTrackTraceDetails?.parent_supplier_user_details?.map(
                (user, index) => {
                  const userKey =
                    user?.id || user?.email || `supplier-user-${index}`;
                  return (
                    <TraceTrackCardWithTitle
                      key={userKey}
                      title={"Suppliers"}
                      data={[
                        {
                          key: `${index + 1} Name`,
                          value: user?.name,
                          keyStyle: { fontWeight: 600 },
                          displayInSingleLine: true,
                        },
                        {
                          key: `  Email`,
                          value: user?.email,
                          keyStyle: { fontWeight: 600, ml: "12px" },
                          displayInSingleLine: true,
                        },
                      ]}
                    />
                  );
                },
              )}
            </Box>
          )}

          {selectedMenuItem === "attributes" && (
            <Box>
              {renderComponentSelector()}
              {Array.isArray(slicedAttributes) &&
              slicedAttributes.length > 0 ? (
                <>
                  <Box
                    sx={{
                      minHeight: "300px",
                      maxHeight: "300px",
                      overflow: "auto",
                    }}
                  >
                    <TraceTrackCardWithTitle
                      title="Attributes"
                      data={slicedAttributes.map((att) => ({
                        key: att.attribute_heading,
                        value: att.attribute_value,
                        valueType: att.attribute_type,
                        keyStyle: { fontWeight: 600 },
                      }))}
                    />
                  </Box>
                  <CustomPaginationV2
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(newPage) => {
                      setPage(newPage);
                    }}
                    totalItems={componentSelfData?.attributes?.length || 0}
                  />
                </>
              ) : (
                <Box
                  sx={{ p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1 }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    No attributes added yet.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}

TraceTrackCards.propTypes = {
  projectID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variantID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedTrackTraceDetails: PropTypes.shape({
    ticket_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ticket_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    reason: PropTypes.string,
    supplier_name: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    ls_details: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    lr_details: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    ee_details: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    swm_details: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    vehicle_system_details: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    vehicle_component_details: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    project_details: PropTypes.shape({
      name: PropTypes.string,
      architecture_name: PropTypes.string,
    }),
    variant_details: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        milestone_details: PropTypes.arrayOf(
          PropTypes.shape({
            milestone_name: PropTypes.string,
          }),
        ),
      }),
    ),
    test_case_details: PropTypes.array,
    oem_user_details: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        email: PropTypes.string,
      }),
    ),
    tech_user_details: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        email: PropTypes.string,
      }),
    ),
    parent_supplier_user_details: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        email: PropTypes.string,
      }),
    ),
  }),
};

export default TraceTrackCards;

const styled = {
  container: {
    height: "100%",
    overflow: "hidden",
  },
  sidebar: {
    height: "100%",
    width: "250px",
    backgroundColor: "#000000",
    color: "#000000",
    overflow: "auto",
    borderRadius: "0 !important",
    border: "none",
    boxShadow: "none",
    margin: 0,
    padding: 0,
  },
  menuItem: {
    color: "#FFFFFF",
    width: "250px",
    height: "42px",
    "&:hover": {
      backgroundColor: "#8888887c",
    },
  },
  menuItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuText: {
    fontStyle: "Open Sans",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
    letterSpacing: "0%",
  },
  contentArea: {
    height: "100%",
    flex: 1,
    overflow: "auto",
    backgroundColor: "#ffffff",
    padding: "0",
    scrollbarGutter: "stable",
  },
  defaultContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};
